import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import IdleScreenTimeModal from "../modal/IdleScreenTimeModal";
import { TICKET_EXTENDED_EVENT } from "../constants/events";
import { clearBrowserStorage } from "../utils/storage";
import {
  DEFAULT_KIOSK_TIMEOUT_MS,
  fetchKioskTimeoutSettings,
} from "../services/kioskTimeoutService";

const EXCLUDED_IDLE_PATHS = ["/", "/qr-scan"] as const;

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "touchstart",
  "click",
  "scroll",
] as const;

const shouldTrackIdle = (pathname: string) =>
  !EXCLUDED_IDLE_PATHS.includes(
    pathname as (typeof EXCLUDED_IDLE_PATHS)[number]
  );

const KioskSessionManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const idleWarningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionExpireTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [idleTimeoutMs, setIdleTimeoutMs] = useState(DEFAULT_KIOSK_TIMEOUT_MS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const showIdleModalRef = useRef(false);
  const idleTimeoutMsRef = useRef(idleTimeoutMs);
  const settingsLoadedRef = useRef(false);
  showIdleModalRef.current = showIdleModal;
  idleTimeoutMsRef.current = idleTimeoutMs;
  settingsLoadedRef.current = settingsLoaded;

  useEffect(() => {
    let cancelled = false;
    fetchKioskTimeoutSettings().then((settings) => {
      if (cancelled) return;
      setIdleTimeoutMs(
        settings.milliseconds > 0 ? settings.milliseconds : DEFAULT_KIOSK_TIMEOUT_MS
      );
      setSettingsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const goToIdle = useCallback(() => {
    setShowIdleModal(false);
    void clearBrowserStorage();
    navigate("/", { replace: true });
  }, [navigate]);

  const clearIdleTimers = useCallback(() => {
    if (idleWarningTimerRef.current) {
      clearTimeout(idleWarningTimerRef.current);
      idleWarningTimerRef.current = null;
    }
    if (sessionExpireTimerRef.current) {
      clearTimeout(sessionExpireTimerRef.current);
      sessionExpireTimerRef.current = null;
    }
  }, []);

  const startSessionExpireTimer = useCallback(() => {
    if (sessionExpireTimerRef.current) {
      clearTimeout(sessionExpireTimerRef.current);
    }

    sessionExpireTimerRef.current = setTimeout(() => {
      goToIdle();
    }, idleTimeoutMsRef.current);
  }, [goToIdle]);

  const showIdleWarning = useCallback(() => {
    setShowIdleModal(true);
    startSessionExpireTimer();
  }, [startSessionExpireTimer]);

  const resetInactivityTimer = useCallback(() => {
    clearIdleTimers();
    setShowIdleModal(false);

    if (!settingsLoadedRef.current || !shouldTrackIdle(location.pathname)) return;

    idleWarningTimerRef.current = setTimeout(() => {
      showIdleWarning();
    }, idleTimeoutMsRef.current);
  }, [clearIdleTimers, location.pathname, showIdleWarning]);

  const handleIdleContinue = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const handleIdleQuit = useCallback(() => {
    clearIdleTimers();
    goToIdle();
  }, [clearIdleTimers, goToIdle]);

  useEffect(() => {
    if (!settingsLoaded || !shouldTrackIdle(location.pathname)) {
      clearIdleTimers();
      setShowIdleModal(false);
      return;
    }

    resetInactivityTimer();

    const onActivity = () => {
      if (showIdleModalRef.current) return;
      resetInactivityTimer();
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, { passive: true })
    );

    return () => {
      clearIdleTimers();
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, onActivity)
      );
    };
  }, [clearIdleTimers, idleTimeoutMs, location.pathname, resetInactivityTimer, settingsLoaded]);

  useEffect(() => {
    const onTicketExtended = () => {
      clearIdleTimers();
      setShowIdleModal(false);
    };

    window.addEventListener(TICKET_EXTENDED_EVENT, onTicketExtended);
    return () => {
      window.removeEventListener(TICKET_EXTENDED_EVENT, onTicketExtended);
    };
  }, [clearIdleTimers]);

  return (
    <IdleScreenTimeModal
      show={showIdleModal}
      onHide={handleIdleContinue}
      onContinue={handleIdleContinue}
      onQuit={handleIdleQuit}
    />
  );
};

export default KioskSessionManager;
